import { useState } from "react";
import { useFormik } from "formik";
import { Box, IconButton, InputAdornment, Link, Typography } from "@mui/material";
import { Check, Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as LinkRouter } from "react-router-dom";

import { BrandedTextField } from "../../../branding/components/BrandedTextField";
import { BrandedButton } from "../../../branding/components/BrandedButton";
import { routesConfig } from "../../../config/routes";
import { useQueryParams } from "../../../hooks/ContextualNavigations";
import { validationSchemaLoginForm } from "../LoginDefault/form/validationSchema";
import { LoginProvider, useLoginContext } from "../LoginDefault/context/LoginContext";
import { BrandedSplitLayout } from "../../../branding/components/BrandedSplitLayout";

// Login genérico guiado pela BrandConfig (layout split). Reusa o submit Cognito do
// LoginDefault (fetchLogin) — nenhuma lógica de auth duplicada. Cores vêm do tema.
// Campos = BrandedTextField (outlined, cores da marca); botão = BrandedButton.
const BrandedForm = (): JSX.Element => {
  const { loginContext } = useLoginContext();
  const { query } = useQueryParams();
  const [showPassword, setShowPassword] = useState(false);

  const { errors, values, touched, isSubmitting, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: { email: "", password: "" },
      validationSchema: validationSchemaLoginForm,
      onSubmit: (vals) => loginContext.fetchLogin(vals),
    });

  return (
    <BrandedSplitLayout>
      <Box>
        <Typography color="primary" sx={{ fontSize: "2.2rem", fontWeight: 700 }}>
          Acesse sua conta
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <BrandedTextField
            type="email"
            id="email"
            name="email"
            label="E-mail"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Digite o email"
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <Check
                      sx={{ color: touched.email && !errors.email ? "success.main" : "transparent" }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Senha + link agrupados com respiro curto (10px) para o link ficar próximo do
              campo a que se refere; o gap: 3 externo mantém o respiro maior antes do botão. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <BrandedTextField
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              label="Senha"
              placeholder="Digite a senha"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowPassword((s) => !s)}>
                      {showPassword ? (
                        <VisibilityOff color="primary" />
                      ) : (
                        <Visibility color="primary" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: "right", fontWeight: "bold", fontSize: "1.4rem" }}>
              <Link
                color="primary.main"
                underline="none"
                component={LinkRouter}
                to={`${routesConfig.FORGOT_PASSWORD}${query}`}
              >
                Esqueceu a senha?
              </Link>
            </Box>
          </Box>

          <BrandedButton type="submit" loading={isSubmitting}>
            Entrar
          </BrandedButton>
        </Box>
      </form>
    </BrandedSplitLayout>
  );
};

const LoginBranded = (): JSX.Element => (
  <LoginProvider>
    <BrandedForm />
  </LoginProvider>
);

export default LoginBranded;
